import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AuthContext from '../components/shared/AuthContext';
import { subscribeRealtimeInvalidate } from '../realtime/realtimeBus';

const STORAGE_KEY = 'qnaread_active_org_id';

export const TeamContext = createContext(null);

export function TeamContextProvider({ children }) {
  const { apiClient, user } = useContext(AuthContext);
  const [organizations, setOrganizations] = useState([]);
  const [access, setAccess] = useState({ teamsEnabled: false, personalPlan: 'FREE' });
  const [activeOrgId, setActiveOrgIdState] = useState(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? Number(raw) : null;
  });
  const [loading, setLoading] = useState(true);
  const [pendingInbox, setPendingInbox] = useState({ pendingCount: 0, items: [] });

  const refreshPendingInbox = useCallback(async () => {
    if (!apiClient || !user) {
      setPendingInbox({ pendingCount: 0, items: [] });
      return;
    }
    try {
      const res = await apiClient.get('/api/organizations/pending-requests?limit=12');
      setPendingInbox({
        pendingCount: res.data?.pendingCount ?? 0,
        items: res.data?.items || [],
      });
    } catch {
      setPendingInbox({ pendingCount: 0, items: [] });
    }
  }, [apiClient, user]);

  const refresh = useCallback(async () => {
    if (!apiClient) return;
    try {
      const [accessRes, listRes] = await Promise.all([
        apiClient.get('/api/organizations/access'),
        apiClient.get('/api/organizations'),
      ]);
      setAccess(accessRes.data || {});
      const list = Array.isArray(listRes.data) ? listRes.data : [];
      setOrganizations(list);
      setActiveOrgIdState((prev) => {
        if (list.length && !list.some((o) => o.id === prev)) {
          localStorage.setItem(STORAGE_KEY, String(list[0].id));
          return list[0].id;
        }
        return prev;
      });
      await refreshPendingInbox();
    } catch {
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  }, [apiClient, refreshPendingInbox]);

  useEffect(() => {
    if (user) refresh();
    else {
      setOrganizations([]);
      setPendingInbox({ pendingCount: 0, items: [] });
      setLoading(false);
    }
  }, [user, refresh]);

  useEffect(() => {
    if (!user || !apiClient) return undefined;
    const tick = () => {
      if (document.visibilityState === 'visible') refreshPendingInbox();
    };
    const interval = window.setInterval(tick, 120000);
    const unsub = subscribeRealtimeInvalidate((d) => {
      if (d?.reason === 'team-request' || d?.reason === 'team-assignment-progress') {
        refreshPendingInbox();
      }
    });
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(interval);
      unsub();
      document.removeEventListener('visibilitychange', tick);
    };
  }, [user, apiClient, refreshPendingInbox]);

  const setActiveOrgId = useCallback((id) => {
    setActiveOrgIdState(id);
    if (id != null) localStorage.setItem(STORAGE_KEY, String(id));
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const activeOrg = useMemo(
    () => organizations.find((o) => o.id === activeOrgId) || null,
    [organizations, activeOrgId]
  );

  const value = useMemo(() => ({
    organizations,
    activeOrg,
    activeOrgId,
    setActiveOrgId,
    access,
    loading,
    refresh,
    pendingInbox,
    pendingRequestCount: pendingInbox.pendingCount ?? 0,
    refreshPendingInbox,
  }), [organizations, activeOrg, activeOrgId, setActiveOrgId, access, loading, refresh, pendingInbox, refreshPendingInbox]);

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}

export function useTeamContext() {
  const ctx = useContext(TeamContext);
  if (!ctx) throw new Error('useTeamContext requires TeamContextProvider');
  return ctx;
}

/** Safe outside strict team pages (returns null if provider missing). */
export function useTeamContextOptional() {
  return useContext(TeamContext);
}
